import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import { CardinalOrientation } from 'walktour'

import { Icon } from '@wartek-id/icon-v2'
import { Button } from '@wartek-id/button-v1'
import { Link } from '@wartek-id/link'
import { Text } from '@wartek-id/text-v1'

import {
  useApiBulkOverridePenilaianKsByPs,
  getPenilaianKsByPsV2QueryKey,
  useApiGetPenilaianKsByPsV2,
  useApiPostPenilaianForDelivery,
} from 'api/kinerja/kepala-sekolah/pengawas-sekolah/penilaian'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import {
  createPenetapanPredikatKinerjaStore,
  DEFAULT_PAGE,
  DEFAULT_STATUS_PENGIRIMAN_FILTER,
  DEFAULT_PREDIKAT_FILTER,
} from 'app/KinerjaShared/stores/penetapan-predikat-kinerja-store'
import PenetapanPredikatTable, {
  showOnlyColumns,
  PREDIKAT_LABELS,
} from 'app/KinerjaShared/components/PenetapanPredikatTable'
import { Header } from 'app/KinerjaShared/components/Header'
import { parseQuery } from 'utils/router'
import { PENGELOLAAN_KINERJA_PS_PATH } from 'app/KinerjaShared/constants/path'
import {
  Onboarding,
  type OnboardingProps,
} from 'app/KinerjaShared/components/Onboarding'
import DialogNoLongerDirectReport from 'app/KinerjaPSPage/components/DialogNoLongerDirectReport'
import DialogUnconfirmedDirectReport from 'app/KinerjaPSPage/components/DialogUnconfirmedDirectReport'
import { generateClipTableHeightForOnboarding } from 'app/KinerjaShared/components/PenetapanPredikatTable/utils'
import usePenetapanTrackEvent from 'app/KinerjaShared/hooks/usePenetapanTrackEvent'

import type {
  PenetapanPredikatKinerjaStoreItem,
  PredikatKinerjaTypes,
  PredikatFilterTypes,
  StatusPengirimanFilterTypes,
} from 'app/KinerjaShared/stores/penetapan-predikat-kinerja-store'
import type { PenilaianItem } from 'types/kinerja-ps'

// -------------------------------------
// Onboarding
// -------------------------------------
const BOTTOM_TOOLBAR_ID = 'penetapan-predikat-kinerja-v2-bottom-toolbar'
const PENETAPAN_ACTION_ID = 'penetapan-predikat-kinerja-v2-penetapan-action'
const KIRIM_ACTION_ID = 'penetapan-predikat-kinerja-v2-kirim-action'
const ONBOARDING_STEPS: OnboardingProps['steps'] = [
  {
    // [data-column='action'] is a rendered colgroup inside the PenetapanPredikatTable
    selector: `[data-column="action"]`,
    description:
      'Klik tombol ini untuk melakukan Rekomendasi Predikat Kinerja dan melihat data yang perlu Anda pertimbangkan.',
    orientationPreferences: [CardinalOrientation.WEST],
    maskSelector: (tourRoot) =>
      generateClipTableHeightForOnboarding(
        tourRoot,
        `[data-column="action"]`,
        `#${BOTTOM_TOOLBAR_ID}`
      ),
  },
  {
    // [data-column='checkbox'] is a rendered colgroup inside the PenetapanPredikatTable
    selector: '[data-column="checkbox"]',
    description: 'Klik kotak ini untuk memilih banyak nama Kepala Sekolah.',
    orientationPreferences: [CardinalOrientation.EAST],
    maskSelector: (tourRoot) =>
      generateClipTableHeightForOnboarding(
        tourRoot,
        `[data-column="checkbox"]`,
        `#${BOTTOM_TOOLBAR_ID}`
      ),
  },
  {
    selector: `#${PENETAPAN_ACTION_ID}`,
    maskSelector: `#${BOTTOM_TOOLBAR_ID}`,
    orientationPreferences: [CardinalOrientation.NORTH],
    tooltipSeparation: 40,
    description:
      'Klik tombol ini untuk melakukan Rekomendasi Predikat Kinerja terhadap banyak Kepala Sekolah yang sudah Anda pilih.',
  },
  {
    selector: `#${KIRIM_ACTION_ID}`,
    maskSelector: `#${BOTTOM_TOOLBAR_ID}`,
    orientationPreferences: [CardinalOrientation.NORTHEAST],
    tooltipSeparation: 40,
    description:
      'Klik tombol ini untuk kirim data Rekomendasi Predikat Kinerja ke Pejabat Penilai Kinerja.',
  },
]

// -------------------------------------
// Penetapan Predikat Kinerja Store
// -------------------------------------
const store = createPenetapanPredikatKinerjaStore({
  isSelectableFn: isSelectableItem,
})

function isSelectableItem(item: PenetapanPredikatKinerjaStoreItem) {
  return (
    item.statusPengiriman === 'NOT_SENT' &&
    item.predikat !== 'UNFINISHED_PENILAIAN' &&
    item.meta?.confirmedSubordinate !== 'REJECTED' &&
    item.meta?.confirmedSubordinate !== 'UNCONFIRMED'
  )
}

export const PARAM_PAGE_KEY = 'page'
export const PARAM_PREDIKAT_KEY = 'predikat'
export const PARAM_STATUS_PENGIRIMAN_KEY = 'status'
export const SEARCH_PARAM_KEY = 'search'

export function PenetapanPredikatKS() {
  const router = useRouter()
  const { periodYear, periodNo } = usePeriodContext()
  const queryClient = useQueryClient()

  // -------------------------------------
  // Resolve filter from query params
  // -------------------------------------
  const resolvedFilter: {
    page: number
    statusPengirimanFilter: StatusPengirimanFilterTypes
    predikatFilter: PredikatFilterTypes
    nameFilter: string
  } = useMemo(() => {
    // Page param
    const pageParam =
      Number(parseQuery<string>(router.query[PARAM_PAGE_KEY])) || DEFAULT_PAGE
    const predikatParam = parseQuery<string>(
      router.query[PARAM_PREDIKAT_KEY]
    ) as PredikatFilterTypes
    const validPredikatFilter: PredikatFilterTypes[] = Object.keys(
      PREDIKAT_LABELS
    ) as PredikatFilterTypes[]
    const isValidPredikatFilter = validPredikatFilter.includes(predikatParam)

    // Status pengiriman param
    const statusPengirimanParam = parseQuery<string>(
      router.query[PARAM_STATUS_PENGIRIMAN_KEY]
    ) as StatusPengirimanFilterTypes
    const validStatusPengirimanFilter: StatusPengirimanFilterTypes[] = [
      'ALL',
      'SENT',
      'NOT_SENT',
    ]
    const isValidStatusPengirimanFilter = validStatusPengirimanFilter.includes(
      statusPengirimanParam
    )

    // Name filter
    const nameFilter = parseQuery<string>(router.query[SEARCH_PARAM_KEY]) || ''

    return {
      page: pageParam,
      statusPengirimanFilter: isValidStatusPengirimanFilter
        ? statusPengirimanParam
        : DEFAULT_STATUS_PENGIRIMAN_FILTER,
      predikatFilter: isValidPredikatFilter
        ? predikatParam
        : DEFAULT_PREDIKAT_FILTER,
      nameFilter: nameFilter,
    }
  }, [router.query])

  // -------------------------------------
  // Store
  // -------------------------------------
  const { init: initStoreItems } = store((store) => ({
    init: store.init,
  }))

  // -------------------------------------
  // Fetch data
  // -------------------------------------
  const { isFetching, isSuccess } = useApiGetPenilaianKsByPsV2({
    params: {
      skpYear: periodYear,
      skpPeriod: periodNo,
    },
    options: {
      onSuccess: (response) => {
        initStoreItems({
          items: response.data.map(mapRawItemToStoreItem),
          page: resolvedFilter.page,
          nameFilter: resolvedFilter.nameFilter,
          predikatFilter: resolvedFilter.predikatFilter,
          statusPengirimanFilter: resolvedFilter.statusPengirimanFilter,
        })
      },
    },
  })

  // -------------------------------------
  // Handle send to dinas
  // -------------------------------------
  const sendMutation = useApiPostPenilaianForDelivery()
  const handleSendToDinas = (
    items: Array<PenetapanPredikatKinerjaStoreItem>
  ) => {
    sendMutation.mutate(
      {
        id: items.map((item) => item.id),
        state: 'PS_TO_DINAS',
      },
      {
        onSuccess: () => {
          // Invalidate cache for the get list query
          queryClient.invalidateQueries(
            getPenilaianKsByPsV2QueryKey({
              skpYear: periodYear,
              skpPeriod: periodNo,
            })
          )
        },
      }
    )
  }

  // -------------------------------------
  // Handle bulk change penetapan
  // -------------------------------------
  const changeMutation = useApiBulkOverridePenilaianKsByPs({
    skpYear: periodYear,
    skpPeriod: periodNo,
  })
  const handleBulkChangePenetapan = (items, data) => {
    changeMutation.mutate(
      items.map((item) => ({
        id: item.id,
        ratingPraktikKinerja: data.praktikKinerja,
        ratingPerilakuKerja: data.perilakuKerja,
      })),
      {
        onSuccess: () => {
          // Invalidate cache for the get list query
          queryClient.invalidateQueries(
            getPenilaianKsByPsV2QueryKey({
              skpYear: periodYear,
              skpPeriod: periodNo,
            })
          )

          // Send tracker
          handleTrackSavePenetapanSuccess()
        },
      }
    )
  }

  // -------------------------------------
  // Track event
  // -------------------------------------
  const { trackEvent } = usePenetapanTrackEvent()

  // Send event tracker when the table is loaded
  useEffect(() => {
    if (!isSuccess) return

    trackEvent({
      activityType: 'view_tabel_pegawai',
      role: 'tk',
      isPlt: false,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess])

  const handleTrackSendToDinasSuccess = useCallback(() => {
    trackEvent({
      activityType: 'send_predikat_kinerja',
      role: 'tk',
      isPlt: false,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTrackSavePenetapanSuccess = useCallback(() => {
    trackEvent({
      activityType: 'save_multiple_predikat_kinerja',
      role: 'tk',
      isPlt: false,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <main className="px-8 pb-32 pt-12 bg-gray-10 min-h-full">
      <div className="max-w-screen-2xl mx-auto">
        <Header
          title="Rekomendasi Predikat Kinerja"
          className="mb-9"
          rightNode={
            <Onboarding
              steps={ONBOARDING_STEPS}
              openOnMount="ONCE"
              storageKey="penetapan-predikat-kinerja-ks-v2-onboarding-done"
              asChild
            >
              <Button
                variant="ghost"
                size="lg"
                color="blue"
                className="space-x-1 !bg-white"
              >
                <Icon color="inherit">help</Icon>
                <span className="text-nowrap">Tata Cara</span>
              </Button>
            </Onboarding>
          }
        />
      </div>

      <div className="max-w-screen-2xl mx-auto">
        <PenetapanPredikatTable.Provider store={store}>
          <section>
            <div className="mb-6 flex flex-col xl:flex-row flex-wrap items-start xl:items-center justify-start xl:justify-between gap-4">
              <div className="flex items-center gap-4">
                <PenetapanPredikatTable.Title title="Daftar Kepala Sekolah" />
                <PenetapanPredikatTable.Search
                  placeholder="Cari"
                  className="w-44"
                />
              </div>

              <div className="flex items-center justify-end gap-4 flex-1">
                <PenetapanPredikatTable.PaginationSize />
                <PenetapanPredikatTable.PredikatFilter
                  paramKey={PARAM_PREDIKAT_KEY}
                />
                <PenetapanPredikatTable.StatusPengirimanFilter
                  paramKey={PARAM_STATUS_PENGIRIMAN_KEY}
                />
                <PenetapanPredikatTable.DownloadAction
                  columns={showOnlyColumns([
                    'name',
                    'jabatan',
                    'satuanPendidikan',
                    'praktikKinerja',
                    'perilakuKerja',
                    'predikatKinerja',
                    'statusPengiriman',
                  ])}
                />
              </div>
            </div>
            <PenetapanPredikatTable.Table
              isLoading={isFetching}
              generateDetailUrl={(item) =>
                `${PENGELOLAAN_KINERJA_PS_PATH}/penetapan/kepala-sekolah/${item.id}`
              }
              columns={showOnlyColumns([
                'checkbox',
                'name',
                'jabatan',
                'satuanPendidikan',
                'praktikKinerja',
                'perilakuKerja',
                'predikatKinerja',
                'statusPengiriman',
                'action',
              ])}
              generateCustomAction={(item) => {
                switch (item.meta?.confirmedSubordinate) {
                  case 'REJECTED':
                    return <RejectedSubordinateAction name={item.name} />
                  case 'UNCONFIRMED':
                    return <UnconfirmedSubordinateAction />
                  default:
                    // Return null to use the default action
                    return null
                }
              }}
            />
            <PenetapanPredikatTable.Pagination
              paramKey={PARAM_PAGE_KEY}
              className="my-7"
              itemLabel="nama"
            />
          </section>

          {/* Bulk Actions */}
          <section
            id={BOTTOM_TOOLBAR_ID}
            style={{ boxShadow: 'var(--elevation-02)' }}
            className="z-10 w-full fixed bottom-0 left-0 right-0 bg-white"
          >
            <PenetapanPredikatTable.SelectionToolbar
              isLoading={isFetching}
              contents={{
                title: 'Nama Dipilih',
                description:
                  'Anda dapat melakukan Rekomendasi Predikat Kinerja untuk banyak nama sekaligus.',
                allSentMessage:
                  'Semua Rekomendasi Predikat Kinerja berhasil dikirim ke Pejabat Penilai Kinerja',
              }}
            >
              <PenetapanPredikatTable.ChangeAction
                id={PENETAPAN_ACTION_ID}
                isLoading={changeMutation.isLoading}
                isError={changeMutation.isError}
                isSuccess={changeMutation.isSuccess}
                contents={{
                  successMessage: 'Berhasil simpan rekomendasi.',
                  errorMessage: 'Gagal simpan rekomendasi. Coba lagi.',
                  bannerTitle:
                    'Rating akan disimpan untuk semua Kepala Sekolah yang dipilih',
                  bannerDescription:
                    'Pastikan Anda sudah cek rekomendasi dan mempertimbangkan kinerja tiap Kepala Sekolah.',
                }}
                onSubmit={handleBulkChangePenetapan}
              >
                Lakukan Sekaligus
              </PenetapanPredikatTable.ChangeAction>
              <PenetapanPredikatTable.SendAction
                id={KIRIM_ACTION_ID}
                contents={{
                  title: 'Konfirmasi untuk Kirim Rekomendasi Predikat Kinerja',
                  description:
                    'Data tidak dapat diubah lagi. Pejabat Penilai Kinerja akan menerima rekomendasi Anda dan melanjutkan proses.',
                  errorMessage:
                    'Gagal kirim Rekomendasi Predikat Kinerja. Coba lagi.',
                  successMessage:
                    'Rekomendasi Predikat Kinerja berhasil dikirim ke Pejabat Penilai Kinerja',
                  loadingMessage: 'Mengirim ke Pejabat Penilai Kinerja...',
                }}
                confirmationText="SAYA SETUJU UNTUK MENGIRIM KE PEJABAT PENILAI KINERJA"
                isError={sendMutation.isError}
                isSuccess={sendMutation.isSuccess}
                onSubmit={handleSendToDinas}
                onSuccess={handleTrackSendToDinasSuccess}
              >
                <Icon color="inverse">send</Icon>
                <span>Kirim ke Pejabat Penilai Kinerja</span>
              </PenetapanPredikatTable.SendAction>
            </PenetapanPredikatTable.SelectionToolbar>
          </section>
        </PenetapanPredikatTable.Provider>
      </div>
    </main>
  )
}

function RejectedSubordinateAction({ name }: { name: string }) {
  const [isOpen, setIsOpen] = useState(false)

  const handleClose = () => {
    setIsOpen(false)
  }

  return (
    <>
      <div className="space-x-2">
        <Text as="span" variant="body-sm">
          Tidak Dapat Dikirim.
        </Text>
        <Link as="button" onClick={() => setIsOpen(true)}>
          Info
        </Link>
      </div>
      <DialogNoLongerDirectReport
        isOpen={isOpen}
        name={name}
        onClose={handleClose}
        onClick={handleClose}
      />
    </>
  )
}

function UnconfirmedSubordinateAction() {
  const [isOpen, setIsOpen] = useState(false)

  const handleClose = () => {
    setIsOpen(false)
  }

  return (
    <>
      <div className="space-x-2">
        <Text as="span" variant="body-sm">
          Tidak Dapat Dikirim.
        </Text>
        <Link as="button" onClick={() => setIsOpen(true)}>
          Info
        </Link>
      </div>
      <DialogUnconfirmedDirectReport
        isOpen={isOpen}
        onClose={handleClose}
        onClick={handleClose}
      />
    </>
  )
}

const mapRawItemToStoreItem = (
  item: PenilaianItem
): PenetapanPredikatKinerjaStoreItem => {
  let predikat: PredikatKinerjaTypes | '' = item.predikatKinerja
  if (item.statePengiriman === 'BELUM_DINILAI') {
    predikat = 'UNFINISHED_PENILAIAN'
  } else if (!predikat) {
    predikat = 'PREDIKAT_NOT_SET'
  }

  const statusPengiriman =
    item.statePengiriman === 'SUDAH_DIKIRIM' ? 'SENT' : 'NOT_SENT'

  return {
    id: item.id,
    name: item.name,
    nip: item.nip,
    satuanPendidikan: item.satuanPendidikan,
    jabatan: item.jabatan,
    perilakuKerja: item.ratingPerilakuKerja,
    praktikKinerja: item.ratingPraktikKinerja,
    predikat,
    statusPengiriman,
    meta: {
      confirmedSubordinate: item.confirmedSubordinate || undefined,
    },
  }
}
